import { Box, Link, Tooltip } from "@mui/material";
import NextLink from "next/link";
import ReactHtmlParser from "@/src/utils/html-parser";
import SpanTypography from "@/src/mui-custom/typography/span-typography/span-typography";
import { getArticleUrl } from "@/src/utils/paths";
import { FONT_UI } from "@/src/components/common/broadsheet/tokens";
import type ArticleIssueNavigation from "@/src/_types/Article/article-issue-navigation";
import type { ArticleIssueNeighbour } from "@/src/_types/Article/article-issue-navigation";

const NeighbourLabel = ({
  label,
  direction,
}: {
  label: string;
  direction: "previous" | "next";
}) => (
  <>
    {direction === "previous" && (
      <SpanTypography aria-hidden="true" sx={{ mr: 0.25 }}>
        &larr;
      </SpanTypography>
    )}
    <SpanTypography
      sx={{
        fontFamily: FONT_UI,
        fontSize: "0.71875rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
      }}
    >
      {label}
      <SpanTypography
        sx={{
          display: { xs: "none", sm: "inline" },
          fontSize: "inherit",
          fontWeight: "inherit",
        }}
      >
        {" in this issue"}
      </SpanTypography>
    </SpanTypography>
    {direction === "next" && (
      <SpanTypography aria-hidden="true" sx={{ ml: 0.25 }}>
        &rarr;
      </SpanTypography>
    )}
  </>
);

const rowSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  maxWidth: "100%",
};

const Neighbour = ({
  neighbour,
  journalAbbr,
  label,
  direction,
}: {
  neighbour: ArticleIssueNeighbour | null;
  journalAbbr: string;
  label: string;
  direction: "previous" | "next";
}) => {
  if (!neighbour) {
    return (
      <Box
        aria-disabled="true"
        sx={{ ...rowSx, color: "text.disabled", cursor: "default" }}
      >
        <NeighbourLabel label={label} direction={direction} />
      </Box>
    );
  }

  return (
    <Tooltip title={ReactHtmlParser(neighbour.title || "")} arrow>
      <Link
        component={NextLink}
        href={getArticleUrl(journalAbbr, neighbour.id)}
        rel={direction === "previous" ? "prev" : "next"}
        sx={rowSx}
      >
        <NeighbourLabel label={label} direction={direction} />
      </Link>
    </Tooltip>
  );
};

const ArticleIssueNavigationBar = ({
  navigation,
  journalAbbr,
}: {
  navigation: ArticleIssueNavigation;
  journalAbbr: string;
}) => {
  const { previous, next } = navigation;

  if (!previous && !next) return null;

  return (
    <Box
      component="nav"
      aria-label="Article navigation within this issue"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        px: 0,
        py: 0.625,
        borderTop: "1px solid",
        borderBottom: "1px solid",
        borderColor: "custom.ruleHairline",
        color: "custom.inkMuted",
        "& a": {
          color: "custom.link",
          textDecoration: "none",
          transition: "all ease-in-out .2s",
          "&:hover": {
            color: "custom.linkHover",
          },
        },
      }}
    >
      <Neighbour
        neighbour={previous}
        journalAbbr={journalAbbr}
        label="Previous article"
        direction="previous"
      />
      <Neighbour
        neighbour={next}
        journalAbbr={journalAbbr}
        label="Next article"
        direction="next"
      />
    </Box>
  );
};

export default ArticleIssueNavigationBar;
